/**
 * Сервис для работы с API этапов (stages)
 */

import authService from './auth.service';
import type {
  Stage,
  StageDetail,
  CreateStagePayload,
  UpdateStagePayload,
  AddServiceTypePayload,
  StageListParams,
  StageActionsResponse,
  StageFlatItem,
  StageFlatItemWithAction,
} from '../types/stage.types';

const API_URL = '/api/v1/stages';

class StageService {
  /**
   * Получить список этапов с опциональными фильтрами
   */
  async list(params?: StageListParams): Promise<Stage[]> {
    const queryParams: string[] = [];

    if (params?.service_types && params.service_types.length > 0) {
      params.service_types.forEach((st) => {
        queryParams.push(`service_types=${encodeURIComponent(st)}`);
      });
    }

    if (params?.include_hidden) {
      queryParams.push('include_hidden=true');
    }

    if (params?.q && params.q.trim()) {
      queryParams.push(`q=${encodeURIComponent(params.q.trim())}`);
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const res = await authService.fetchWithAuth('/api/v1/stages' + queryString);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список этапов');
    }
    return await res.json();
  }

  /**
   * Получить этап по ID
   */
  async getById(id: number): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Этап не найден');
    }
    return await res.json();
  }

  /**
   * Получить этап по коду
   */
  async getByCode(code: string): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/by-code/${code}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Этап не найден');
    }
    return await res.json();
  }

  /**
   * Создать новый этап
   */
  async create(payload: CreateStagePayload): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать этап');
    }
    return await res.json();
  }

  /**
   * Обновить этап (частичное обновление)
   */
  async update(id: number, payload: UpdateStagePayload): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить этап');
    }
    return await res.json();
  }

  /**
   * Удалить этап (soft delete)
   */
  async delete(id: number): Promise<{ detail: string }> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить этап');
    }
    return await res.json();
  }

  /**
   * Восстановить удаленный этап
   */
  async restore(id: number): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}/restore`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось восстановить этап');
    }
    return await res.json();
  }

  /**
   * Получить список service_types для этапа
   */
  async getServiceTypes(id: number): Promise<string[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}/service-types`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить типы сервисов');
    }
    return await res.json();
  }

  /**
   * Добавить service_type к этапу
   */
  async addServiceType(id: number, payload: AddServiceTypePayload): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}/service-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось добавить тип сервиса');
    }
    return await res.json();
  }

  /**
   * Удалить service_type из этапа
   */
  async removeServiceType(id: number, serviceType: string): Promise<StageDetail> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}/service-types/${serviceType}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить тип сервиса');
    }
    return await res.json();
  }

  /**
   * Получить связанные действия этапа
   */
  async getStageActions(stageId: number, includeHidden = true): Promise<StageActionsResponse> {
    const queryString = includeHidden ? '?include_hidden=true' : '';
    const res = await authService.fetchWithAuth(`${API_URL}/${stageId}/actions${queryString}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить действия этапа');
    }
    return await res.json();
  }

  /**
   * Получить плоский список этапов для service_type (один запрос)
   */
  async getFlatStages(serviceType: string, includeHidden = false): Promise<StageFlatItem[]> {
    const params = new URLSearchParams({
      service_type: serviceType,
    });
    if (includeHidden) {
      params.append('include_hidden', 'true');
    }
    const res = await authService.fetchWithAuth(`${API_URL}/stages?${params.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить этапы');
    }
    return await res.json();
  }

  /**
   * Получить этапы, привязанные к действию (с доп. полями is_required, is_blocking)
   */
  async getFlatStagesForAction(
    serviceType: string,
    actionId: number,
    includeHidden = true
  ): Promise<StageFlatItemWithAction[]> {
    const params = new URLSearchParams({
      service_type: serviceType,
      action_id: actionId.toString(),
    });
    if (includeHidden) {
      params.append('include_hidden', 'true');
    }
    const res = await authService.fetchWithAuth(`${API_URL}/stages?${params.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить этапы действия');
    }
    return await res.json();
  }
}

export default new StageService();
